import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import useModals from '@/v2/hooks/useModals'
import { useInvoiceActions } from '@/v2/hooks/useInvoiceActions'
import { useCallback } from 'react'
import { isAfter } from 'date-fns'
import { usePatientFinancePaymentMethods } from '@/v2/hooks/usePatientFinance'
import { useUser } from '@/v2/hooks/useUser'

export const useInvoiceOptions = ({ invoice, isInstallPayment = false }) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const { patient } = usePatientContext()
  const { goTo } = useModals()
  const {
    sendPaymentLink,
    cancelInvoice,
    scheduleInvoice,
  } = useInvoiceActions({ patientId: patient.id })
  const { user } = useUser()

  const { creditCards, bankAccounts } = usePatientFinancePaymentMethods({
    patientId: patient.id,
  })

  const hasPaymentMethods =
    (creditCards && creditCards.length > 0) ||
    (bankAccounts && bankAccounts.length > 0)

  const hasCheckPayments = invoice.payments.some(
    payment =>
      payment.paymentMethod === 'check' && payment.status !== 'declined'
  )

  const isInvoiceSchedulable =
    !invoice.overdue &&
    isAfter(new Date(invoice.dueDate), new Date()) &&
    invoice.paymentMethodId

  const awaitingPaymentItems = user?.admin
    ? [
        {
          label: 'Send Payment Link',
          onClick: async () => {
            try {
              await sendPaymentLink({ invoiceId: invoice.id })
              onOpenSnackbar('Payment Link Sent.')
            } catch (error) {
              onOpenSnackbar(error.message, 'error')
            }
          },
        },
        {
          label: 'Cancel Invoice',
          onClick: async () => {
            try {
              await cancelInvoice({ invoiceId: invoice.id })
              onOpenSnackbar('Invoice Canceled.')
            } catch (error) {
              onOpenSnackbar(error.message, 'error')
            }
          },
        },
      ]
    : [
        {
          label: 'Send Payment Link',
          onClick: async () => {
            try {
              await sendPaymentLink({ invoiceId: invoice.id })
              onOpenSnackbar('Payment Link Sent.')
            } catch (error) {
              onOpenSnackbar(error.message, 'error')
            }
          },
        },
      ]

  const deniedItems = user?.admin
    ? [
        {
          label: 'Cancel Invoice',
          onClick: async () => {
            try {
              await cancelInvoice({ invoiceId: invoice.id })
              onOpenSnackbar('Invoice Canceled.')
            } catch (error) {
              onOpenSnackbar(error.message, 'error')
            }
          },
        },
      ]
    : []

  const declineCheckOption = hasCheckPayments
    ? [
        {
          label: 'Decline Check',
          onClick: () =>
            goTo(`/v2/patient/${patient.id}/finance/decline-payment`, {
              invoice,
            }),
        },
      ]
    : []

  const paidItems = user?.admin
    ? [
        {
          label: 'Refund',
          onClick: () =>
            goTo(`/v2/patient/${patient.id}/finance/refund-payment`, {
              invoice,
            }),
        },
        ...declineCheckOption,
      ]
    : []

  const chargeNowOption = [
    {
      label: 'Charge now',
      onClick: () =>
        goTo(`/v2/patient/${patient.id}/finance/change-payment-method`, {
          invoice,
          type: 'charge',
        }),
    },
  ]

  const scheduleInvoiceOption = isInvoiceSchedulable
    ? [
        {
          label: 'Schedule to due date',
          onClick: async () => {
            try {
              if (confirm('Are you sure you want to schedule this invoice?')) {
                await scheduleInvoice({ invoiceId: invoice.id })
                onOpenSnackbar('Invoice scheduled.')
              }
            } catch (error) {
              onOpenSnackbar(error.message, 'error')
            }
          },
        },
      ]
    : []

  const viewInvoiceOption = [
    {
      label: 'View invoice',
      target: '_blank',
      href: { pathname: invoice.publicUrl },
    },
  ]

  const changePaymentMethodOption = hasPaymentMethods
    ? [
        {
          label: 'Change Payment Method',
          onClick: () =>
            goTo(`/v2/patient/${patient.id}/finance/change-payment-method`, {
              invoice,
              type: 'change',
              isInstallPayment,
            }),
        },
      ]
    : []

  const editDueDateOption = user?.admin
    ? [
        {
          label: 'Edit Due Date',
          onClick: () =>
            goTo(`/v2/patient/${patient.id}/finance/edit-invoice-due-date`, {
              invoice,
            }),
        },
      ]
    : []

  const getOptionsBasedOnInvoiceStatus = useCallback(() => {
    const isInvoicePaid = invoice.payments.some(
      payment => payment.status === 'processed'
    )

    switch (invoice.status) {
      case 'awaiting_process':
        return [
          ...viewInvoiceOption,
          ...chargeNowOption,
          ...changePaymentMethodOption,
          ...editDueDateOption,
          ...awaitingPaymentItems,
        ]
      case 'awaiting_payment':
        return isInvoicePaid
          ? [
              ...viewInvoiceOption,
              ...chargeNowOption,
              ...changePaymentMethodOption,
              ...scheduleInvoiceOption,
              ...editDueDateOption,
              ...paidItems,
              ...awaitingPaymentItems,
            ]
          : [
              ...viewInvoiceOption,
              ...chargeNowOption,
              ...changePaymentMethodOption,
              ...scheduleInvoiceOption,
              ...editDueDateOption,
              ...awaitingPaymentItems,
            ]
      case 'payment_confirmed':
        return [...viewInvoiceOption, ...paidItems]
      case 'denied':
        return [...deniedItems]
      case 'refunded':
        return [...viewInvoiceOption]
      case 'partially_refunded':
        return [...viewInvoiceOption, ...paidItems]
      case 'canceled':
        return [...viewInvoiceOption]
      default:
        return [
          ...viewInvoiceOption,
          ...changePaymentMethodOption,
          ...awaitingPaymentItems,
        ]
    }
  }, [
    invoice,
    viewInvoiceOption,
    chargeNowOption,
    changePaymentMethodOption,
    awaitingPaymentItems,
    scheduleInvoiceOption,
    editDueDateOption,
    paidItems,
    deniedItems,
  ])

  return getOptionsBasedOnInvoiceStatus()
}
